import React from "react";
import { Pressable, StyleSheet, View, ViewStyle } from "react-native";

import { PencilIcon } from "@/components/atoms/PencilIcon";
import { Typography } from "@/components/atoms/Typography";
import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";

export interface ProfileContactCardWebProps {
  title: string;
  value: React.ReactNode;
  helperText?: string;
  onEdit?: () => void;
  /**
   * Optional extra style for outer container (used to make side-by-side layout on web).
   */
  containerStyle?: ViewStyle;
  rightElement?: React.ReactNode;
}

/**
 * Reusable contact detail card (login email, phone, communication email).
 * Extracted from the mobile PersonalDetailsScreen and reused on web Account & Security.
 */
export function ProfileContactCardWeb({
  title,
  value,
  helperText,
  onEdit,
  containerStyle,
  rightElement,
}: ProfileContactCardWebProps) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();

  return (
    <View
      style={[
        styles.card,
        { backgroundColor: colors.backgroundLevel2 },
        containerStyle,
      ]}
    >
      <View style={{ gap: 8, flex: 1, minWidth: 0 }}>
        <Typography
          variant="headline"
          color="primary"
          style={{ textAlign: isRTL ? "right" : "left" }}
        >
          {title}
        </Typography>
        {value}
        {helperText ? (
          <Typography
            variant="caption"
            color="tertiary"
            style={{ textAlign: isRTL ? "right" : "left" }}
          >
            {helperText}
          </Typography>
        ) : null}
      </View>
      {rightElement}
      {onEdit && (
        <Pressable
          hitSlop={8}
          style={{ padding: 4, marginLeft: 8 }}
          onPress={onEdit}
        >
          <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
            <PencilIcon size={20} color={colors.fontPrimary} />
          </View>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 16,
    gap: 8,
    flexDirection: "row",
    alignItems: "center",
  },
});

export default ProfileContactCardWeb;
